import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, RefreshCw, AlertTriangle } from "lucide-react";

function useBlockerCounts() {
  const [blockers, setBlockers] = useState({});
  useEffect(() => {
    const fetchCounts = () => api.get("/admin/blocker-counts").then(({ data }) => setBlockers(data)).catch(() => {});
    fetchCounts();
    window.addEventListener("blockers-changed", fetchCounts);
    return () => window.removeEventListener("blockers-changed", fetchCounts);
  }, []);
  return blockers;
}

function ClientTabsEmpty({ status, error, onRetry }) {
  if (status === "loading") {
    return (
      <p data-testid="clients-loading" className="flex items-center gap-2 text-[#6B7280] mb-6">
        <Loader2 size={16} className="animate-spin" style={{ color: "#F97316" }} /> Loading clients…
      </p>
    );
  }
  if (status === "error") {
    return (
      <div data-testid="clients-load-error" className="rounded-lg border p-4 mb-6 text-sm flex items-center justify-between gap-3"
        style={{ borderColor: "#FCA5A5", backgroundColor: "#FEF2F2", color: "#B91C1C" }}>
        <span>Couldn't load your clients ({error}). This is a connection or server issue — your clients are safe.</span>
        <button data-testid="clients-retry-button" onClick={onRetry}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-semibold text-white flex-shrink-0"
          style={{ backgroundColor: "#B91C1C" }}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }
  return <p className="text-[#6B7280]">No active clients. Create a client first.</p>;
}

function ClientTabButton({ c, isActive, warn, onChange }) {
  return (
    <button
      data-testid={`client-tab-${c.id}`}
      onClick={() => onChange(c.id)}
      className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2 flex items-center gap-1.5"
      style={isActive ? { color: "#F97316", borderColor: "#F97316" } : { color: "#6B7280", borderColor: "transparent" }}
    >
      {c.name}
      {warn > 0 && (
        <span data-testid={`client-tab-blockers-${c.id}`} title={`${warn} item(s) flagged as holding up the build`}
          className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: "#ffe606", color: "#0A1F44" }}>
          <AlertTriangle size={10} />{warn}
        </span>
      )}
    </button>
  );
}

export default function ClientTabs({ clients, active, onChange, status, error, onRetry }) {
  const blockers = useBlockerCounts();
  if (!clients.length) return <ClientTabsEmpty status={status} error={error} onRetry={onRetry} />;
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 mb-6 border-b" style={{ borderColor: "#E5E7EB" }}>
      {clients.map((c) => (
        <ClientTabButton key={c.id} c={c} isActive={active === c.id} warn={blockers[c.id] || 0} onChange={onChange} />
      ))}
    </div>
  );
}
